import React, { useState, useEffect } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import DataTable from "react-data-table-component";
import { get, post } from "../../services/api";
import Select from "react-select";
import { toast } from "react-toastify";
import { customTableStyles } from "../../assets/CustomTableStyles";
import styles from "./styles.module.css";

function Timetable() {
  const token = localStorage.getItem("token");
  const organizationId = localStorage.getItem("organizationId");
  const savedCenterId =
    localStorage.getItem("centerId") === "null"
      ? null
      : localStorage.getItem("centerId");

  const [triggerRerender, setTriggerRerender] = useState(false);

  const [selectedCenterId, setSelectedCenterId] = useState(
    savedCenterId || null
  );
  const [selectedClassSectionId, setSelectedClassSectionId] = useState(null);
  const [classSectionsData, setClassSectionsData] = useState([]);
  const [centersData, setCentersData] = useState([]);
  const [timetable, setTimetable] = useState([]);

  const {
    register,
    formState: { errors, isSubmitting },
    control,
    handleSubmit,
    watch,
    setValue,
    getValues,
    trigger,
    reset,
    setError,
  } = useForm({
    defaultValues: {},
  });

  useEffect(() => {
    const fetchCenters = async () => {
      try {
        const response = await get(
          `Dropdown/GetAllCentersByOrganizationId?organizationId=${organizationId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setCentersData(response);

        if (savedCenterId) {
          const selectedCenter = response.find(
            (center) => center.id === parseInt(savedCenterId, 10)
          );

          if (selectedCenter) {
            setValue("centerId", {
              value: selectedCenter.id,
              label: selectedCenter.name,
            });
          }
        }
      } catch (error) {
        console.error("Error fetching centers:", error);
      }
    };

    fetchCenters();
  }, [selectedCenterId]);

  const fetchClassSectionsByCenterId = async (centerId) => {
    try {
      const response = await get(
        `ClassSection/GetAllClassSectionByCenterId?centerId=${centerId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setClassSectionsData(response);
    } catch (error) {
      console.error("Error fetching classes:", error);
    }
  };

  useEffect(() => {
    if (selectedCenterId) {
      fetchClassSectionsByCenterId(selectedCenterId);
    }
  }, [selectedCenterId]);

  useEffect(() => {
    const fetchTimetables = async () => {
      try {
        const response = await get(
          `SMSApp/GetTimeTableByCenterClassSectionId?centerId=${selectedCenterId}&classSectionid=${selectedClassSectionId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setTimetable(response?.timetable);
      } catch (error) {
        console.error("Error fetching centers:", error);
      }
    };

    if (selectedCenterId && selectedClassSectionId) {
      fetchTimetables();
    }
  }, [selectedCenterId, selectedClassSectionId, triggerRerender]);

  const daysOfWeek = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    // "Sunday",
  ];
  const timetableData = {};

  daysOfWeek.forEach((day) => {
    timetableData[day] = [];
  });

  timetable.forEach((item) => {
    const dayName = item.dayName;
    if (timetableData[dayName]) {
      timetableData[dayName].push(item);
    }
  });

  return (
    <>
      <div className="page-header d-flex mb-2">
        <h2 className="page-heading fs-7">Timetable</h2>
      </div>

      <div className="card">
        <div className="card-body">
          <div className="row g-3 mb-3">
            <div className="col-md-2">
              <label className="form-label">
                Center<span className="text-danger fs-9">*</span>
              </label>
              <Controller
                name="centerId"
                control={control}
                rules={{ required: "This field is required." }}
                render={({ field }) => (
                  <Select
                    {...field}
                    options={centersData.map((center) => ({
                      value: center.id,
                      label: center.name,
                    }))}
                    isDisabled={savedCenterId !== "0"}
                    onChange={(selectedOption, actionMeta) => {
                      if (actionMeta.action === "clear") {
                        setValue("centerId", null);
                        setValue("classSectionId", null);
                        setClassSectionsData([]);
                      } else {
                        setValue("centerId", selectedOption);
                        setValue("classSectionId", null);
                        setSelectedCenterId(selectedOption.value);
                        fetchClassSectionsByCenterId(selectedOption.value);
                      }
                      trigger("centerId");
                    }}
                    className="react-select-style"
                    classNamePrefix="react-select-style-pref"
                    isClearable={!savedCenterId}
                  />
                )}
              />
              {errors.centerId && (
                <small className="text-danger">{errors.centerId.message}</small>
              )}
            </div>
            <div className="col-md-2">
              <label className="form-label">
                Class - Section<span className="text-danger fs-9">*</span>
              </label>
              <Controller
                name="classSectionId"
                control={control}
                // rules={{ required: "This field is required." }}
                render={({ field }) => (
                  <Select
                    {...field}
                    options={classSectionsData.map((classSection) => ({
                      value: classSection.id,
                      label: classSection.name,
                    }))}
                    onChange={(selectedOption, actionMeta) => {
                      if (actionMeta.action === "clear") {
                        setValue("classSectionId", null);
                        setTimetable([]);
                      } else {
                        setValue("classSectionId", selectedOption);
                        setSelectedClassSectionId(selectedOption.value);
                        setTriggerRerender((prev) => !prev);
                      }
                      trigger("classSectionId");
                    }}
                    className="react-select-style"
                    classNamePrefix="react-select-style-pref"
                    isClearable
                  />
                )}
              />
              {errors.classSectionId && (
                <small className="text-danger">
                  {errors.classSectionId.message}
                </small>
              )}
            </div>
          </div>

          {timetable.length ? (
            <table className={`${styles[("table", "timetableStyle")]}`}>
              <thead>
                <tr>
                  <th>Period No.</th>
                  {daysOfWeek.map((day) => (
                    <th key={day}>{day}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {timetableData[daysOfWeek[0]].map((_, index) => (
                  <tr key={index}>
                    <th>
                      <div className="">{index + 1}</div>
                    </th>
                    {daysOfWeek.map((day) => {
                      const entry = timetableData[day][index];
                      return (
                        <td key={day}>
                          {entry ? (
                            <div className={styles.periodCell}>
                              <div
                                className={`fs-9 fw-bold mb-1 text-uppercase ${styles["periodName"]}`}
                              >
                                {entry.name}
                              </div>
                              <div className="fs-9 fw-semibold">
                                {entry.fromTime} - {entry.toTime}
                              </div>
                              <div className="fs-9 fw-semibold">
                                {entry.roomName}
                              </div>
                            </div>
                          ) : (
                            ""
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="text-center fs-9">
              There are no records to display
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default Timetable;
